import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Plus, Crown, ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getInterests, setInterests, apiUpdateZodiac } from "@/lib/api";
import { finishOnboarding } from "@/lib/membership";
import { takeAfterAuth } from "@/components/CouponOfDay";
import { CitySearch } from "@/components/CitySearch";
import { ZODIAC_SIGNS, displayCat } from "@/lib/readoo";

const chip = (on) => `inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border text-sm font-semibold transition-colors ${on ? "border-brand bg-brand text-onBrand" : "border-hair bg-surfaceSecondary text-onSurface hover:border-brand/50"}`;

function Step({ n, total, title, children, onNext, onSkip, nextLabel = "Avanti", busy }) {
  return (
    <div data-testid={`onboarding-step-${n}`}>
      <p className="font-mono text-[11px] font-bold uppercase tracking-wider text-brand">Passo {n} di {total}</p>
      <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-semibold text-onSurface">{title}</h1>
      <div className="mt-6">{children}</div>
      <div className="mt-8 flex items-center gap-3">
        <button type="button" onClick={onNext} disabled={busy} data-testid={`onboarding-next-${n}`}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand text-onBrand text-sm font-bold uppercase tracking-wide hover:bg-brandSecondary disabled:opacity-60 transition-colors">
          {busy ? <Loader2 size={16} className="animate-spin" /> : null} {nextLabel} <ArrowRight size={15} />
        </button>
        <button type="button" onClick={onSkip} data-testid={`onboarding-skip-${n}`} className="text-sm font-semibold text-subtle hover:text-brand">Salta</button>
      </div>
    </div>
  );
}

// Onboarding breve dopo la registrazione: usa gli stessi interessi, squadre, città e segno del profilo.
export default function Onboarding() {
  const navigate = useNavigate();
  const { user, loading, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [cats, setCats] = useState(new Set());
  const [tags, setTags] = useState(new Set());
  const [sign, setSign] = useState(null);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [need, setNeed] = useState(null); // calcolato una volta: la città scelta al passo 3 non deve cambiare i passi

  useEffect(() => { if (!loading && !user) navigate("/registrati", { replace: true }); }, [loading, user, navigate]);
  useEffect(() => { if (user && !need) setNeed({ city: !user.meteo_city, sign: !user.zodiac_sign }); }, [user, need]);
  useEffect(() => {
    if (!user) return;
    getInterests().then((r) => { setData(r); setCats(new Set(r.categories)); setTags(new Set(r.tags)); }).catch(() => setData({ available: [], available_tags: [] }));
  }, [user]);
  if (!user || !data || !need) return <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-brand" /></div>;

  const needCity = need.city, needSign = need.sign;
  const steps = needCity || needSign ? 3 : 2;
  const toggle = (set, setter, v) => { const n = new Set(set); n.has(v) ? n.delete(v) : n.add(v); setter(n); };
  const saveInterests = async () => {
    setBusy(true);
    try { await setInterests([...cats], [...tags]); } catch {} finally { setBusy(false); }
  };
  const next = async (save) => {
    if (save && step <= 2) await saveInterests();
    if (save && step === 3 && sign) await apiUpdateZodiac(sign).catch(() => {});
    if (step >= steps) { await finishOnboarding().catch(() => {}); refresh(); setStep(4); }
    else setStep(step + 1);
  };
  const done = () => navigate(takeAfterAuth() || "/per-te", { replace: true });

  return (
    <div data-testid="onboarding-page" className="max-w-[760px] mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
      {step === 1 && (
        <Step n={1} total={steps} title="Cosa ti interessa?" busy={busy} onNext={() => next(true)} onSkip={() => next(false)}>
          <div className="flex flex-wrap gap-2">
            {data.available.map((c) => (
              <button key={c} type="button" onClick={() => toggle(cats, setCats, c)} data-testid={`onboarding-cat-${c}`} aria-pressed={cats.has(c)} className={chip(cats.has(c))}>
                {cats.has(c) ? <Check size={13} /> : <Plus size={13} />} {displayCat(c)}
              </button>
            ))}
          </div>
        </Step>
      )}
      {step === 2 && (
        <Step n={2} total={steps} title="Segui la tua squadra" busy={busy} onNext={() => next(true)} onSkip={() => next(false)}>
          <div className="flex flex-wrap gap-2">
            {data.available_tags.map((t) => (
              <button key={t.slug} type="button" onClick={() => toggle(tags, setTags, t.slug)} data-testid={`onboarding-team-${t.slug}`} aria-pressed={tags.has(t.slug)} className={chip(tags.has(t.slug))}>
                {tags.has(t.slug) ? <Check size={13} /> : <Plus size={13} />} {t.label}
              </button>
            ))}
          </div>
        </Step>
      )}
      {step === 3 && (
        <Step n={3} total={steps} title="Personalizza il tuo Readoo" busy={busy} onNext={() => next(true)} onSkip={() => next(false)}>
          {needCity && (
            <div className="mb-6">
              <p className="text-sm font-semibold text-onSurface mb-2">La tua città per il meteo</p>
              <CitySearch onDone={() => refresh()} />
            </div>
          )}
          {needSign && (
            <div>
              <p className="text-sm font-semibold text-onSurface mb-2">Il tuo segno zodiacale</p>
              <div className="flex flex-wrap gap-2">
                {ZODIAC_SIGNS.map((z) => (
                  <button key={z.key} type="button" onClick={() => setSign(sign === z.key ? null : z.key)} data-testid={`onboarding-sign-${z.key}`} aria-pressed={sign === z.key} className={chip(sign === z.key)}>
                    {z.emoji} {z.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </Step>
      )}
      {step === 4 && (
        <div data-testid="onboarding-done">
          <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-onSurface">Il tuo Readoo è pronto.</h1>
          {user.founder && (
            <p data-testid="onboarding-founder" className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand text-onBrand text-sm font-bold"><Crown size={15} /> Sei Founder Readoo.</p>
          )}
          <p className="mt-4 text-base text-onSurfaceSecondary">Interessi, squadre, meteo e oroscopo si cambiano quando vuoi dal tuo profilo.</p>
          <button type="button" onClick={done} data-testid="onboarding-go"
            className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-brand text-onBrand text-sm font-bold uppercase tracking-wide hover:bg-brandSecondary transition-colors">
            Vai al tuo Readoo <ArrowRight size={15} />
          </button>
        </div>
      )}
    </div>
  );
}
